import type { ReactNode } from 'react';
import { cn } from './cn';

export interface ActionBarProps {
  /** The one action this view exists to produce. Always solid magenta. */
  primary: ReactNode;
  /** Supporting action — Message, See availability. */
  secondary?: ReactNode;
  /** Save control. */
  save?: ReactNode;
  /**
   * Alternative contact channels — Telegram, WhatsApp, Signal.
   * Deliberately demoted: small, muted, below the fold of the action block.
   */
  channels?: ReactNode;
  /** Pin to the bottom of the viewport on mobile, in the thumb zone. */
  sticky?: boolean;
  className?: string;
}

/**
 * The action hierarchy, shared.
 *
 *   primary        the thing you want tapped
 *   secondary      the supporting action
 *   save           keep for later
 *   channels       everything else, visibly quieter
 *
 * `.men` already had the right *structure* here — it was the clearest thing on
 * either site — but its Telegram / WhatsApp / Signal row competed visually with
 * the booking button. `.app` had the opposite problem: "Send a Signal" was
 * enormous and its supporting actions nearly invisible.
 *
 * Channels render at 10px muted with no fill, so they read as routes rather
 * than as choices competing with the primary.
 */
export const ActionBar = ({
  primary,
  secondary,
  save,
  channels,
  sticky,
  className,
}: ActionBarProps) => (
  <div
    className={cn(
      'flex flex-col gap-3',
      sticky && [
        'sticky bottom-0 z-30 -mx-4 px-4 pt-3',
        'border-t border-white/[0.06] bg-hottr-ink/90 backdrop-blur-xl',
        'pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
      ],
      className,
    )}
  >
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">{primary}</div>
      {save}
    </div>

    {secondary && <div className="flex items-center gap-2">{secondary}</div>}

    {channels && (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">{channels}</div>
    )}
  </div>
);

export interface ChannelLinkProps {
  icon: ReactNode;
  label: string;
  href: string;
  /** Fires before navigation — analytics hook. */
  onClick?: () => void;
  className?: string;
}

/** A single demoted contact route. */
export const ChannelLink = ({ icon, label, href, onClick, className }: ChannelLinkProps) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1.5 rounded-sm',
      'font-display text-[10px] font-bold uppercase tracking-hottr-label text-white/45',
      'transition-colors duration-base ease-mechanical hover:text-white',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink focus-visible:ring-offset-2 focus-visible:ring-offset-hottr-ink',
      className,
    )}
  >
    {icon}
    {label}
  </a>
);
